import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BotData,
  BotType,
  Direction,
  LanguageNameSpaces,
  LongShort,
  MarginTypes,
  OpenMessage,
} from "@shared/types";
import {
  BaseBotFormValues,
  BotFormType,
  BulkEditBotData,
  Error,
  GridType,
} from "@shared/features/bots/BotForm/common/types";
import {
  EXCHANGES,
  LONG_SHORT_TO_DISPLAY,
  MARGIN_TYPE_TO_DISPLAY,
  MAX_LEVERAGE_CROSS_EXCHANGES,
} from "@shared/common/constants";
import {
  BotFormContext,
  PreserveBotFormValuesContext,
} from "@shared/features/bots/BotForm/common/BotForm.context";
import { LONG_SHORT_OPTIONS } from "@shared/features/bots/BotForm/common/constants";
import {
  getOptionNameByValue,
  getOptionValueByName,
} from "@shared/features/bots/utils";
import {
  checkValidOverlapValue,
  getBulkCreateMarginOptions,
  validateLeverage,
} from "@shared/features/bots/BotForm/common/utils";
import { isSymbol } from "@shared/types/utils";
import useFormHelperData from "@shared/features/bots/BotForm/common/hooks/useFormHelperData";
import { difference } from "lodash";
import { useClientsData } from "@shared/utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  useGetBulkEditMarginOptions,
  useMarginOptions,
} from "@shared/features/bots/BotForm/common/hooks";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface useMarginSelectorsProps {
  openMessage: OpenMessage;
}

const useMarginSelectors = ({ openMessage }: useMarginSelectorsProps) => {
  const preserveValues = useContext(PreserveBotFormValuesContext);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botGeneralData,
    longShort,
    setLongShort,
    symbols,
    exchanges,
    useSymbolFromAlert,
    multiplier,
    setMultiplier,
    marginType,
    setMarginType,
    addError,
    removeError,
    account: formAccount,
    botFormType,
    isSymbolValid,
    getBotCreateGeneralDataStatus,
    publicBotGeneralData,
    bulkActionBotsData,
    botType,
    isAdvancedView,
    isEdit,
    gridsData,
    accountsOverlapData,
    isChannelConfiguration,
    isLeverageExchanges,
    isBacktesting,
  } = useContext(BotFormContext)!;
  const { activeClientsData } = useClientsData();
  const { getHelperData } = useFormHelperData();
  const parsedLongShort = isSymbol(longShort) ? undefined : longShort;
  const account = isSymbol(formAccount) ? undefined : formAccount;
  const [savedLongShortValue, setSavedLongShortValue] = useState<
    LongShort[] | undefined
  >();
  const { getMarginOptions } = useMarginOptions();
  const { getBulkEditMarginOptions } = useGetBulkEditMarginOptions();

  const marginInfoRef = useRef<{
    longShort?: LongShort[];
    marginType?: BaseBotFormValues["marginType"];
    multiplier?: BaseBotFormValues["multiplier"];
  }>(undefined);
  marginInfoRef.current = {
    multiplier,
    marginType,
    longShort: parsedLongShort,
  };
  const [initializedFormData, setInitializedFormData] = useState<
    { exchange?: string; symbol?: string } | undefined
  >();
  const [lastUserValue, setLastUserValues] = useState<
    | {
        longShort?: LongShort[];
        marginType?: BaseBotFormValues["marginType"];
        multiplier?: BaseBotFormValues["multiplier"];
      }
    | undefined
  >();
  const symbol = isSymbol(symbols) ? undefined : symbols?.[0];
  const isPublicBotCopy = botFormType === BotFormType.COPY_PUBLIC;
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const isBulkCreate = botFormType === BotFormType.BULK_CREATE;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isMaxLeverageCrossExchange =
    account && MAX_LEVERAGE_CROSS_EXCHANGES.includes(account.exchange);
  const showGridBotDirectionChangeWarning =
    isGridBot && isEdit && gridsData?.gridType === GridType.CUSTOM;
  const defaultDirections = useMemo(
    () =>
      isSignalsBot || isHedgingBot
        ? [LongShort.LONG, LongShort.SHORT]
        : [LongShort.LONG],
    [isSignalsBot, isHedgingBot]
  );

  const { marginTypeOptions, multiplierOptions, longShortOptions } =
    useMemo(() => {
      if (isChannelConfiguration && !isLeverageExchanges) return {};
      if (isBulkEdit || isSymbol(symbols)) {
        return getBulkEditMarginOptions(
          bulkActionBotsData as BulkEditBotData[]
        );
      } else if (isBulkCreate) {
        return getBulkCreateMarginOptions(accountsOverlapData);
      }
      return getMarginOptions(
        botGeneralData?.options,
        useSymbolFromAlert ? undefined : symbols,
        exchanges,
        account?.exchange
      );
    }, [
      bulkActionBotsData,
      symbols,
      botGeneralData?.options,
      isLeverageExchanges,
      isChannelConfiguration,
      useSymbolFromAlert,
      exchanges,
      useSymbolFromAlert,
      account,
      getMarginOptions,
    ]);

  const getValidMultiplier = (multiplier: number) => {
    const validMultipliers = multiplierOptions
      ?.map((option) => option.value)
      .filter((iterationMultiplier) => iterationMultiplier <= multiplier)
      .sort((a, b) => b - a);
    return validMultipliers?.[0];
  };

  const shouldInitialize = () => {
    const formInitialized =
      !!botGeneralData &&
      !getBotCreateGeneralDataStatus.isFetching &&
      (!!account || isTradingViewSignalsBot) &&
      !!symbol &&
      isSymbolValid;
    const formValuesChanged =
      symbol !== initializedFormData?.symbol ||
      account?.exchange !== initializedFormData?.exchange;
    const isValidBotFormType = botFormType !== BotFormType.BULK_EDIT;
    const isLeverageValuesInitialized =
      !!marginInfoRef.current?.longShort &&
      (isSignalsBot ||
        (!!marginInfoRef.current?.marginType &&
          !!marginInfoRef.current?.multiplier));
    return (
      isValidBotFormType &&
      formInitialized &&
      (!isLeverageValuesInitialized || formValuesChanged)
    );
  };

  const getOriginalBotLeverageData = (
    botData: BotData
  ): [LongShort[] | undefined, MarginTypes | undefined, number | undefined] => {
    const directions =
      botData.directions ??
      (botData.options["Long/Short"]?.length ? defaultDirections : undefined);
    return [
      directions,
      botData.margin_type ?? undefined,
      botData.leverage ?? undefined,
    ];
  };

  const getDefaultLeverageData = (): [
    LongShort[] | undefined,
    BaseBotFormValues["marginType"],
    BaseBotFormValues["multiplier"]
  ] => {
    let defaultLongShort: LongShort[] | undefined =
      (!!lastUserValue?.longShort && marginInfoRef.current?.longShort) ||
      defaultDirections;
    let defaultMarginType: BaseBotFormValues["marginType"] =
      (!!lastUserValue?.marginType && marginInfoRef.current?.marginType) ||
      marginTypeOptions?.[0]?.value;
    let defaultMultiplier: BaseBotFormValues["multiplier"] =
      (!!lastUserValue?.multiplier && marginInfoRef.current?.multiplier) ||
      multiplierOptions?.[0]?.value;
    return [defaultLongShort, defaultMarginType, defaultMultiplier];
  };

  useEffect(() => {
    if (!shouldInitialize()) return;
    let updatedLongShort: LongShort[] | undefined;
    let updatedMarginType: BaseBotFormValues["marginType"];
    let updatedMultiplier: BaseBotFormValues["multiplier"];
    [updatedLongShort, updatedMarginType, updatedMultiplier] =
      getDefaultLeverageData();
    if (
      ![BotFormType.BULK_CREATE, BotFormType.CREATE].includes(botFormType) ||
      isBacktesting
    ) {
      const [originalLongShort, originalMarginType, originalMultiplier] =
        getOriginalBotLeverageData(botGeneralData as BotData);
      if (!lastUserValue?.longShort) updatedLongShort = originalLongShort;
      if (!lastUserValue?.marginType) updatedMarginType = originalMarginType;
      if (!lastUserValue?.multiplier) updatedMultiplier = originalMultiplier;
    }
    if (
      [BotFormType.COPY_PUBLIC, BotFormType.CREATE].includes(botFormType) &&
      !isBacktesting
    ) {
      if (
        !lastUserValue?.longShort &&
        !longShortOptions?.find((option) =>
          updatedLongShort?.includes(option.value)
        )
      ) {
        updatedLongShort = defaultDirections;
      }
      if (!lastUserValue?.multiplier && !isSymbol(updatedMultiplier)) {
        updatedMultiplier = getValidMultiplier(updatedMultiplier || 1);
      }
      if (
        !lastUserValue?.marginType &&
        !marginTypeOptions?.find((option) => option.value === updatedMarginType)
      ) {
        updatedMarginType = marginTypeOptions?.[0]?.value;
      }
    }
    updatedLongShort = longShortOptions?.length ? updatedLongShort : undefined;
    updatedMarginType = marginTypeOptions?.length
      ? updatedMarginType
      : undefined;
    updatedMultiplier = multiplierOptions?.length
      ? updatedMultiplier
      : undefined;
    if (
      isMaxLeverageCrossExchange &&
      updatedMarginType === MarginTypes.CROSS &&
      multiplierOptions?.length
    ) {
      updatedMultiplier = multiplierOptions[multiplierOptions.length - 1].value;
    }
    if (preserveValues && !initializedFormData) {
      if (
        parsedLongShort?.every((value) =>
          longShortOptions?.some((option) => option.value === value)
        )
      )
        updatedLongShort = parsedLongShort;
      if (marginTypeOptions?.some((option) => option.value === marginType))
        updatedMarginType = marginType;
      if (
        multiplier !== undefined &&
        (!multiplierOptions?.length ||
          multiplierOptions.some((option) => option.value === multiplier))
      )
        updatedMultiplier = multiplier;
    }
    setLongShort(
      updatedLongShort &&
        (Array.isArray(updatedLongShort)
          ? updatedLongShort
          : [updatedLongShort])
    );
    if (!isSignalsBot) {
      setMarginType(updatedMarginType);
      setMultiplier(updatedMultiplier);
    }
    setInitializedFormData({ exchange: account?.exchange, symbol });
  }, [
    botGeneralData,
    symbols,
    account,
    longShortOptions,
    marginTypeOptions,
    multiplierOptions,
    getBotCreateGeneralDataStatus,
    isBacktesting,
  ]);

  const errorMessages = useMemo(() => {
    if (
      !isSymbolValid ||
      isSymbol(longShort) ||
      isSymbol(marginType) ||
      isSymbol(multiplier)
    )
      return [];
    return validateLeverage(
      marginTypeOptions,
      multiplierOptions,
      longShortOptions,
      longShort?.[0],
      marginType,
      multiplier,
      symbol,
      account,
      botType,
      botFormType,
      t
    );
  }, [
    longShortOptions,
    longShort,
    marginTypeOptions,
    marginType,
    multiplierOptions,
    multiplier,
    isSymbolValid,
    t,
  ]);

  useEffect(() => {
    if (errorMessages.length > 0) {
      addError(Error.INVALID_LEVERAGE);
    } else {
      removeError(Error.INVALID_LEVERAGE);
    }
  }, [errorMessages]);

  const onMarginTypeChange = useCallback(
    (value: MarginTypes) => {
      if (
        !checkValidOverlapValue(
          accountsOverlapData,
          "marginType",
          value,
          openMessage,
          t
        )
      ) {
        return;
      }
      setMarginType(value);
      let lastUserMultiplier = lastUserValue?.multiplier;
      if (
        multiplierOptions?.length &&
        value === MarginTypes.CROSS &&
        isMaxLeverageCrossExchange
      ) {
        lastUserMultiplier =
          multiplierOptions[multiplierOptions.length - 1].value;
        setMultiplier(lastUserMultiplier);
      }
      setLastUserValues((lastUserValue) => ({
        ...lastUserValue,
        marginType: value,
        multiplier: lastUserMultiplier,
      }));
    },
    [
      accountsOverlapData,
      openMessage,
      t,
      setMarginType,
      lastUserValue?.multiplier,
      multiplierOptions,
      isMaxLeverageCrossExchange,
      setMultiplier,
    ]
  );

  const onMultiplierDataChange = useCallback(
    (value: number) => {
      setMultiplier(value);
      setLastUserValues((lastUserValue) => ({
        ...lastUserValue,
        multiplier: value,
      }));
    },
    [setMultiplier]
  );

  const handleLongShortChange = (longShort: LongShort[]) => {
    setLongShort(longShort);
    setLastUserValues((lastUserValue) => ({
      ...lastUserValue,
      longShort,
    }));
  };

  const onLongShortChange = (value?: string | string[]) => {
    let parsedValue: string[] = [];
    if (value) {
      parsedValue = Array.isArray(value) ? value : [value];
    }
    const longShort = parsedValue.map(
      (longShortValue) =>
        getOptionValueByName(longShortValue, LONG_SHORT_OPTIONS) as LongShort
    );
    const isValid = longShort?.every((value) =>
      checkValidOverlapValue(
        accountsOverlapData,
        "longShort",
        value,
        openMessage,
        t
      )
    );
    if (isValid) {
      if (showGridBotDirectionChangeWarning) {
        setSavedLongShortValue(longShort);
      } else {
        handleLongShortChange(longShort);
      }
    }
  };

  const multiplierData = useMemo(() => {
    return {
      options: multiplierOptions?.map((option) => ({
        value: option.value,
        label: option.name,
      })),
      disabled:
        !multiplierOptions?.length ||
        (isHedgingBot && isEdit) ||
        (marginType === MarginTypes.CROSS && isMaxLeverageCrossExchange),
      shouldDisplay:
        !isSignalsBot &&
        (isSymbol(multiplier) || multiplierOptions?.length || isPublicBotCopy),
      onChange: onMultiplierDataChange,
      value: isSymbol(multiplier) ? undefined : multiplier || 1,
      placeholder: isSymbol(multiplier) ? t("labels.multi") : undefined,
      title: t("labels.multiplier"),
    };
  }, [
    multiplierOptions,
    marginType,
    isMaxLeverageCrossExchange,
    isPublicBotCopy,
    multiplier,
    t,
    onMultiplierDataChange,
    isHedgingBot,
    isEdit,
    isSignalsBot,
  ]);

  const getLongShortData = () => {
    const isMultiValue = isSymbol(longShort);
    let defaultValue: Direction[] | undefined = [];
    if (isMultiValue) defaultValue = undefined;
    else if (longShort?.length) {
      defaultValue = longShort.map((longShortValue) => {
        return getOptionNameByValue(
          longShortValue,
          LONG_SHORT_OPTIONS
        ) as Direction;
      });
    } else if (!longShortOptions?.length && isPublicBotCopy && !isSignalsBot) {
      defaultValue = [Direction.LONG];
    }
    return {
      shouldDisplay:
        !isHedgingBot &&
        !!activeClientsData?.length &&
        (isMultiValue ||
          longShortOptions?.length ||
          (isPublicBotCopy && !isSignalsBot)),
      disabled: !longShortOptions?.length,
      options: LONG_SHORT_OPTIONS.map((option) => option.name),
      onChange: onLongShortChange,
      defaultValue: isSignalsBot ? defaultValue : defaultValue?.[0],
      title: t("labels.direction"),
      isDropdown: isBulkEdit || isSignalsBot,
      isMultiSelect: isSignalsBot,
      placeholder: isMultiValue ? t("labels.multi") : undefined,
      helperData: getHelperData("longShort"),
    };
  };
  const longShortData = getLongShortData();

  const marginTypeData = useMemo(
    () => ({
      shouldDisplay:
        !isSignalsBot &&
        (isSymbol(marginType) || marginTypeOptions?.length || isPublicBotCopy),
      value: isSymbol(marginType) ? undefined : marginType || "Isolated",
      placeholder: isSymbol(marginType) ? t("labels.multi") : undefined,
      onChange: onMarginTypeChange,
      disabled: !marginTypeOptions?.length || (isHedgingBot && isEdit),
      options: marginTypeOptions,
      title: t("labels.margin_type"),
    }),
    [
      marginTypeOptions,
      isPublicBotCopy,
      marginType,
      multiplierData,
      t,
      onMarginTypeChange,
      isSignalsBot,
      isHedgingBot,
      isEdit,
    ]
  );

  const gridBotDirectionChangeModalData = {
    isOpen: !!savedLongShortValue,
    title: t("messages.warnings.grid_direction_change_title"),
    content: t("messages.warnings.grid_direction_change_content"),
    onCancel: () => setSavedLongShortValue(undefined),
    onOk: () => {
      if (savedLongShortValue) {
        handleLongShortChange(savedLongShortValue);
        setSavedLongShortValue(undefined);
      }
    },
  };

  const shouldShowWarnings =
    initializedFormData &&
    symbol &&
    (account || isTradingViewSignalsBot) &&
    isSymbolValid;
  const warnings: [string, boolean][] | undefined = useMemo(() => {
    if (!shouldShowWarnings) return;
    const warnings: [string, boolean][] = [];
    errorMessages.forEach((errorMessage) =>
      warnings.push([errorMessage, true])
    );
    if (account?.exchange === EXCHANGES.FTX_FUTURES) {
      warnings.push([t("messages.warnings.ftx_shared_leverage"), false]);
    }
    if (isPublicBotCopy && publicBotGeneralData && account) {
      const {
        margin_type: originalMarginType,
        leverage: originalMultiplier,
        directions: originalDirections,
      } = publicBotGeneralData;

      const currentLongShort = parsedLongShort ?? [LongShort.LONG];
      const missingDirections = difference(
        originalDirections,
        currentLongShort
      );
      if (
        (!lastUserValue?.longShort ||
          (!longShortOptions &&
            lastUserValue?.longShort?.length !==
              (parsedLongShort?.length || LongShort.LONG))) &&
        missingDirections.length
      ) {
        warnings.push([
          t("messages.warnings.direction_changed", {
            direction: translateOptionName(
              LONG_SHORT_TO_DISPLAY[missingDirections[0]]
            ),
            exchange: account.exchange,
          }),
          false,
        ]);
      }
      if (
        (!lastUserValue?.marginType ||
          (!marginTypeData.options &&
            lastUserValue?.marginType !==
              (marginType || MarginTypes.ISOLATED))) &&
        originalMarginType &&
        (marginType || MarginTypes.ISOLATED) !== originalMarginType
      ) {
        warnings.push([
          t("messages.warnings.margin_type_changed", {
            marginType: translateOptionName(
              MARGIN_TYPE_TO_DISPLAY[originalMarginType]
            ),
            exchange: account.exchange,
          }),
          false,
        ]);
      }
      if (
        (!lastUserValue?.multiplier ||
          (!multiplierData.options &&
            lastUserValue?.multiplier !== (multiplier || 1))) &&
        originalMultiplier &&
        (multiplier || 1) !== originalMultiplier
      ) {
        warnings.push([
          t("messages.warnings.multiplier_changed", {
            multiplier: originalMultiplier,
            symbol,
            exchange: account.exchange,
          }),
          false,
        ]);
      }
    }

    if (warnings.length > 1) {
      return warnings.map((warning, index) => [
        `${index + 1}. ${warning[0]}`,
        warning[1],
      ]);
    }
    return warnings;
  }, [
    shouldShowWarnings,
    errorMessages,
    account,
    lastUserValue,
    multiplierData,
    marginTypeData,
    publicBotGeneralData,
    parsedLongShort,
    longShortOptions,
    isPublicBotCopy,
    marginType,
    multiplier,
    symbol,
    t,
  ]);

  return {
    initializedFormData,
    errorMessages,
    lastUserValue,
    setLastUserValues,
    longShortOptions,
    isPublicBotCopy,
    isMaxLeverageCrossExchange,
    symbol,
    multiplierData,
    longShortData,
    marginTypeData,
    warnings,
    isAdvancedView,
    gridBotDirectionChangeModalData,
    isSignalsBot,
    isTradingViewSignalsBot,
  };
};

export default useMarginSelectors;
